// Captures the browser's install prompt once, at module load, independent of App.tsx's
// state — this used to live inside App's constructor gated behind a `forPwa` flag that
// only ever ran on a separate pwa.<domain> subdomain most users never visited. Now that
// installability is app-wide, any component can ask usePwaInstallPrompt() whether the
// browser is offering to install, without threading state through App.tsx's routing.
import { useEffect, useState } from 'react';

interface IBeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: IBeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e as IBeforeInstallPromptEvent;
  notify();
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  notify();
});

export function usePwaInstallPrompt(): { canInstall: boolean; promptInstall: () => Promise<void> } {
  const [canInstall, setCanInstall] = useState(deferredPrompt !== null);

  useEffect(() => {
    const listener = () => setCanInstall(deferredPrompt !== null);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) {
      return;
    }
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    notify();
  };

  return { canInstall, promptInstall };
}
